<template>
  <div
    :class="['consent-node-label', { 'consent-node-label--clickable': isClickable }]"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
    :tabindex="isClickable ? 0 : -1"
    :role="isClickable ? 'button' : undefined"
    :aria-label="ariaLabel"
  >
    <v-icon
      :class="iconColorClass"
      small
    >
      {{ node.icon }}
    </v-icon>
    <v-tooltip bottom :disabled="!isTruncated">
      <template v-slot:activator="{ on, attrs }">
        <span
          ref="labelText"
          class="consent-node-label__text"
          v-bind="attrs"
          v-on="on"
        >
          {{ node.name }}
        </span>
      </template>
      <span>{{ node.name }}</span>
    </v-tooltip>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import type { PropType } from 'vue'
import { ConsentNodeType, NODE_TYPE_CONFIG } from './types'
import type { ConsentStructureNode } from './types'

export default Vue.extend({
  name: 'ConsentNodeLabel',

  props: {
    /** The consent structure node to render */
    node: {
      type: Object as PropType<ConsentStructureNode>,
      required: true
    },
    /** Whether to allow clicks on brand nodes (default: false) */
    allowBrandClick: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isTruncated: false,
      debouncedCheckTruncation: null as (() => void) | null
    }
  },

  computed: {
    /**
     * Determines if this node is clickable
     * Brand nodes are not clickable by default (unless allowBrandClick is true)
     */
    isClickable(): boolean {
      if (this.allowBrandClick) {
        return true
      }
      return this.node.type !== ConsentNodeType.BRAND
    },

    /**
     * Returns the CSS class for the icon colour
     */
    iconColorClass(): string {
      return this.node.color || ''
    },

    /**
     * Generates an accessible label for screen readers
     * Uses NODE_TYPE_CONFIG for consistent labelling
     */
    ariaLabel(): string {
      const config = NODE_TYPE_CONFIG[this.node.type]
      const typeLabel = config?.label || 'Node'
      const clickHint = this.isClickable ? ', click to view details' : ''
      return `${typeLabel}: ${this.node.name}${clickHint}`
    }
  },

  mounted() {
    this.checkTruncation()
    // Debounced resize handler to avoid performance issues with many nodes
    this.debouncedCheckTruncation = this.debounce(this.checkTruncation, 150)
    window.addEventListener('resize', this.debouncedCheckTruncation)
  },

  beforeDestroy() {
    if (this.debouncedCheckTruncation) {
      window.removeEventListener('resize', this.debouncedCheckTruncation)
    }
  },

  updated() {
    // Re-check when component updates (e.g., node name changes)
    this.$nextTick(() => {
      this.checkTruncation()
    })
  },

  methods: {
    /**
     * Handles click events on the node label
     */
    handleClick(event: MouseEvent | KeyboardEvent): void {
      if (!this.isClickable) {
        return
      }
      event.stopPropagation()
      this.$emit('click', {
        node: this.node,
        type: this.node.type,
        event
      })
    },

    /**
     * Checks if the text element is truncated by comparing scrollWidth with clientWidth
     */
    checkTruncation(): void {
      const element = this.$refs.labelText as HTMLElement | undefined
      if (element) {
        // Add a small tolerance (1px) to account for sub-pixel rendering
        this.isTruncated = element.scrollWidth > element.clientWidth + 1
      }
    },

    /**
     * Simple debounce utility to prevent excessive function calls
     */
    debounce<T extends (...args: unknown[]) => void>(fn: T, delay: number): T {
      let timeoutId: ReturnType<typeof setTimeout> | null = null
      return ((...args: unknown[]) => {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
        timeoutId = setTimeout(() => fn.apply(this, args), delay)
      }) as T
    }
  }
})
</script>

<style scoped lang="scss">
@import './theme.scss';

.consent-node-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  transition: background-color 0.15s ease;
  user-select: none;
}

.consent-node-label--clickable {
  cursor: pointer;
}

.consent-node-label--clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.consent-node-label--clickable:focus {
  outline: 2px solid var(--primary, #1976d2);
  outline-offset: 2px;
}

.consent-node-label__text {
  font-size: 14px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.87);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

/* Theme colours - imported from shared theme.scss */
.consent-node-label >>> {
  @include consent-theme-colours;
}
</style>
